/**
 * Each ad channel's sync behind the /looker "● data as of" pill (replaces the
 * retired "Data freshness" app box, 2026-10-13). Runs for every workspace view,
 * whatever the template holds; fetches nothing in the master's all-workspaces
 * scope. Same run-key rules as the app boxes (template + scope + period +
 * language), and a stale result is never shown.
 *
 * @module hooks/useLookerChannelFreshness
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadChannelFreshness } from '@/services/looker/lookerAppLoaders';
import type { FreshChannel } from '@/services/looker/lookerAppData';
import type { LookerDashboardPayload } from '@/services/looker/lookerTypes';
import { logError } from '@/services/logger';
import { payloadRunKey } from './useLookerAppTiles';

export type ChannelFreshnessState =
  /** No payload, or the all-workspaces scope: nothing to say, nothing fetched. */
  | { status: 'idle' }
  | { status: 'loading' }
  /** channels [] = no ad channel connected. */
  | { status: 'ready'; channels: FreshChannel[] }
  /** The read failed: never shown as fresh. */
  | { status: 'failed' };

type Settled = Exclude<ChannelFreshnessState, { status: 'idle' } | { status: 'loading' }>;

export const useLookerChannelFreshness = (payload: LookerDashboardPayload | null): ChannelFreshnessState => {
  const { i18n } = useTranslation();
  const workspaceId = payload?.scope.kind === 'workspace' ? payload.scope.workspaceId : null;
  const runKey = payload && workspaceId ? payloadRunKey(payload, i18n.language) : '';
  const [result, setResult] = useState<{ key: string; state: Settled } | null>(null);

  useEffect(() => {
    if (!runKey || !workspaceId) return;
    let ignore = false;
    loadChannelFreshness(workspaceId).then(
      (channels) => {
        if (!ignore) setResult({ key: runKey, state: { status: 'ready', channels } });
      },
      (err: unknown) => {
        logError('LookerChannelFreshness', 'sync status read failed', err);
        if (!ignore) setResult({ key: runKey, state: { status: 'failed' } });
      },
    );
    return () => {
      ignore = true;
    };
  }, [runKey, workspaceId]);

  return useMemo<ChannelFreshnessState>(() => {
    if (!runKey) return { status: 'idle' };
    return result && result.key === runKey ? result.state : { status: 'loading' };
  }, [runKey, result]);
};
